/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useMemo, useEffect, useCallback } from 'react'
import { Typography, Icon, Button, Stack, EmptyState, Modal } from '@mdsol/onex-design'
// @ts-expect-error - createColumnHelper is exported but not in TypeScript definitions
import { DataGrid, createColumnHelper } from '@mdsol/onex-design/DataGrid'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import { getFileReferences, deleteDocumentRefs, getFilePath, type PathItem } from '../../../services/api/files'
import { useEsignature } from '../../../hooks/useEsignature'
import { useModal } from '../../../contexts/ModalContext'
import AddReferencesModal from './addReferences/AddReferencesModal'
import { toast } from '../../../utils/toast'
import { USER_ROLE } from '../../../constants/user'
import { PAGE_SIZES_ARRAY, DEFAULT_PAGE_SIZE } from '../../../constants/pagination'
import styles from './DocumentReferences.module.css'
import { MedidataLogo } from '../../../components/MedidataLogo'
import { buildUrlWithCSelections } from '~/utils/navigation'

const MAX_REFERENCES = 100

// Roles that cannot add/modify references
const READ_ONLY_ROLES = [
  USER_ROLE.AUDITOR,
  USER_ROLE.INSPECTOR,
  USER_ROLE.ETMF_STUDY_USER,
  USER_ROLE.ETMF_SITE_USER,
  USER_ROLE.ETMF_SPONSOR
]

interface EnrichedReference {
  referenceFsiId: number
  referenceType: string
  filename: string
  artifact: string
  product: string
  study: string
  environment: string
  country: string
  site: string
}

// [Change 1] Replaced LocationState with component props for modal-based usage
interface DocumentReferencesModalProps {
  fsiId: number
  fsiName: string
  canManageReferences: boolean
  onClose?: () => void
}

/**
 * Parse ETMF hierarchy from file path.
 * Path structure: Root / [Clinical|Medidata] / Product / Study / Environment / Country / Site / Zone / Section / ...
 * The "Clinical" or "Medidata" folder at index 1 must be skipped to reach the actual hierarchy levels.
 */
function parseEtmfHierarchy(pathItems: PathItem[]): {
  product: string
  study: string
  environment: string
  country: string
  site: string
} {
  const names = pathItems.map((p) => p.name)
  // Skip the "Clinical" or "Medidata" folder at index 1 if present
  const secondSegment = names[1]?.toLowerCase()
  const offset = secondSegment === 'clinical' || secondSegment === 'medidata' ? 2 : 1
  return {
    product: names[offset] || '-',
    study: names[offset + 1] || '-',
    environment: names[offset + 2] || '-',
    country: names[offset + 3] || '-',
    site: names[offset + 4] || '-'
  }
}

const columnHelper = createColumnHelper()

const DocumentReferencesModal: React.FC<DocumentReferencesModalProps> = ({
  fsiId,
  fsiName,
  canManageReferences,
  onClose
}) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { showModal } = useModal()
  const { signAction } = useEsignature()

  const [isOpen, setIsOpen] = useState(true)

  // RBAC: Check if the current user role is read-only
  const currentRole = context.service.auth.getRole()
  const isReadOnly = !canManageReferences || READ_ONLY_ROLES.includes(currentRole as string)

  // References state
  const [references, setReferences] = useState<EnrichedReference[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE })
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([])
  const [globalSearchValue, setGlobalSearchValue] = useState('')
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [selectAllError, setSelectAllError] = useState<string | null>(null)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)
  const [isBulkDeletePending, setIsBulkDeletePending] = useState(false)

  // [Fix #1] eTMF context for the base document — pre-fills AddReferencesModal filters
  const [documentEtmfContext, setDocumentEtmfContext] = useState<
    | {
        product?: string
        study?: string
        environment?: string
        country?: string
        site?: string
      }
    | undefined
  >(undefined)

  // Load references (API now returns enriched fields directly)
  const loadReferences = useCallback(async () => {
    if (!fsiId) return

    setIsLoading(true)
    try {
      const sortParam = sorting.length > 0 ? sorting[0] : undefined
      const response = await getFileReferences(fsiId, {
        ...(sortParam ? { sortBy: sortParam.id, sortDesc: sortParam.desc } : {}),
        pageStart: pagination.pageIndex,
        pageSize: pagination.pageSize,
        searchText: globalSearchValue ?? ''
      })
      setTotalElements(response.totalElements)
      setReferences(
        response.elements.map((ref) => ({
          referenceFsiId: ref.referenceFsiId,
          referenceType: ref.referenceType,
          filename: ref.filename,
          artifact: ref.artifact || '-',
          product: ref.product || '-',
          study: ref.study || '-',
          environment: ref.environment || '-',
          country: ref.country || '-',
          site: ref.site || '-'
        }))
      )
    } catch (error) {
      console.error('Failed to load references:', error)
      toast.error(t('ERROR'))
    } finally {
      setIsLoading(false)
    }
  }, [fsiId, sorting, pagination.pageIndex, pagination.pageSize, t, globalSearchValue])

  // [Fix #1] Load eTMF hierarchy for the base document to pre-fill AddReferencesModal context.
  // Only pre-populate context filters when the document resides under a "clinical" path.
  useEffect(() => {
    if (!fsiId) return
    getFilePath(fsiId)
      .then((pathItems) => {
        // Check if any segment in the path contains "clinical" (case-insensitive)
        const isClinicalPath = pathItems.some((item) => item.name.toLowerCase().includes('clinical'))

        if (!isClinicalPath) {
          // Not a clinical/eTMF document — leave context undefined (filters stay default)
          return
        }

        const hierarchy = parseEtmfHierarchy(pathItems)
        // Only set context if hierarchy values are meaningful (not '-')
        const ctx: { product?: string; study?: string; environment?: string; country?: string; site?: string } = {}
        if (hierarchy.product && hierarchy.product !== '-') ctx.product = hierarchy.product
        if (hierarchy.study && hierarchy.study !== '-') ctx.study = hierarchy.study
        if (hierarchy.environment && hierarchy.environment !== '-') ctx.environment = hierarchy.environment
        if (hierarchy.country && hierarchy.country !== '-') ctx.country = hierarchy.country
        if (hierarchy.site && hierarchy.site !== '-') ctx.site = hierarchy.site
        if (Object.keys(ctx).length > 0) setDocumentEtmfContext(ctx)
      })
      .catch(() => {
        // Not an eTMF document — leave context undefined
      })
  }, [fsiId])

  useEffect(() => {
    loadReferences()
  }, [loadReferences])

  // Filtered references (global search)
  const filteredReferences = useMemo(() => {
    if (!globalSearchValue) return references
    const search = globalSearchValue.toLowerCase()
    return references.filter(
      (ref) =>
        ref.filename.toLowerCase().includes(search) ||
        ref.artifact.toLowerCase().includes(search) ||
        ref.product.toLowerCase().includes(search) ||
        ref.study.toLowerCase().includes(search) ||
        ref.environment.toLowerCase().includes(search) ||
        ref.country.toLowerCase().includes(search) ||
        ref.site.toLowerCase().includes(search)
    )
  }, [references, globalSearchValue])

  const existingLinkedIds = useMemo(
    () => Array.from(new Set(references.map((ref) => ref.referenceFsiId))),
    [references]
  )

  // Handle remove reference — shows confirmation dialog before deleting
  const handleRemoveReference = useCallback((referenceFsiId: number) => {
    setPendingDeleteId(referenceFsiId)
    setIsBulkDeletePending(false)
    setShowConfirmationDialog(true)
  }, [])

  // Handle adding references via the new AddReferencesModal
  const handleAddReferences = useCallback(() => {
    showModal(
      <AddReferencesModal
        fsiId={fsiId}
        etmfContext={documentEtmfContext}
        canManageReferences={canManageReferences}
        requireESign={context.service.auth.requireESignatureForFileCommands()}
        existingLinkedIds={existingLinkedIds}
        onConfirm={() => {
          // Refresh the references grid after successful add
          loadReferences()
        }}
        onClose={() => {}}
      />,
      { keepParentModalsOpen: true }
    )
  }, [
    fsiId,
    showModal,
    canManageReferences,
    documentEtmfContext,
    context.service.auth,
    loadReferences,
    existingLinkedIds
  ])

  // Handle bulk remove of selected references — shows confirmation dialog before deleting
  const handleBulkRemoveReferences = useCallback(() => {
    const selectedIds = Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map(Number)
    if (selectedIds.length === 0) return
    setIsBulkDeletePending(true)
    setPendingDeleteId(null)
    setShowConfirmationDialog(true)
  }, [rowSelection])

  // Perform the actual delete after confirmation
  const handleLeavePage = useCallback(async () => {
    setShowConfirmationDialog(false)

    if (isBulkDeletePending) {
      if (!fsiId) return
      const selectedIds = Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map(Number)
      if (selectedIds.length === 0) return

      const refsToRemove = references.filter((ref) => selectedIds.includes(ref.referenceFsiId))
      const payload = refsToRemove.map((ref) => ({
        filename: ref.filename,
        referenceFsiId: ref.referenceFsiId,
        referenceType: ref.referenceType,
        obsolete: false
      }))

      try {
        if (context.service.auth.requireESignatureForFileCommands()) {
          const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_UPDATE_REFERENCES')
          await deleteDocumentRefs(fsiId, payload, validation)
        } else {
          await deleteDocumentRefs(fsiId, payload)
        }
        setReferences((prev) => prev.filter((ref) => !selectedIds.includes(ref.referenceFsiId)))
        setRowSelection({})
        toast.success(
          t('REFERENCES_REMOVED_SUCCESS', { defaultValue: 'The selected reference(s) were removed' }),
          'bottom-right'
        )
      } catch (error) {
        console.error('Failed to remove references:', error)
        toast.error(t('ERROR'))
      } finally {
        setIsBulkDeletePending(false)
      }
    } else if (pendingDeleteId !== null) {
      if (!fsiId) return
      const refToRemove = references.find((ref) => ref.referenceFsiId === pendingDeleteId)
      if (!refToRemove) return

      const payload = [
        {
          filename: refToRemove.filename,
          referenceFsiId: refToRemove.referenceFsiId,
          referenceType: refToRemove.referenceType,
          obsolete: false
        }
      ]

      try {
        if (context.service.auth.requireESignatureForFileCommands()) {
          const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_UPDATE_REFERENCES')
          await deleteDocumentRefs(fsiId, payload, validation)
        } else {
          await deleteDocumentRefs(fsiId, payload)
        }
        setReferences((prev) => prev.filter((ref) => ref.referenceFsiId !== pendingDeleteId))
        setRowSelection({})
        toast.success(
          t('REFERENCES_REMOVED_SUCCESS', { defaultValue: 'The selected reference(s) were removed' }),
          'bottom-right'
        )
      } catch (error) {
        console.error('Failed to remove reference:', error)
        toast.error(t('ERROR'))
      } finally {
        setPendingDeleteId(null)
      }
    }
  }, [fsiId, isBulkDeletePending, pendingDeleteId, references, rowSelection, context.service.auth, signAction, t])

  const handleCancel = useCallback(() => {
    setShowConfirmationDialog(false)
    setPendingDeleteId(null)
    setIsBulkDeletePending(false)
  }, [])

  // The DataGrid passes a plain selection object (not an updater).
  // Validate MAX_REFERENCES in a separate derived effect to avoid
  // impure side effects inside a state updater.
  const handleRowSelectionChange = useCallback((newSelection: Record<string, boolean>) => {
    setRowSelection(newSelection ?? {})
  }, [])

  // Handler for "Select all" bulk action — selects all rows on the current page
  const handleSelectAllOnPage = useCallback(() => {
    const allSelection: Record<string, boolean> = {}
    filteredReferences.forEach((ref) => {
      allSelection[String(ref.referenceFsiId)] = true
    })
    setRowSelection(allSelection)
  }, [filteredReferences])

  // Handler for "Clear selection" bulk action
  const handleClearSelection = useCallback(() => {
    setRowSelection({})
  }, [])

  // Derived count of currently selected references
  const selectedCount = useMemo(() => Object.values(rowSelection).filter(Boolean).length, [rowSelection])

  useEffect(() => {
    if (selectedCount > MAX_REFERENCES) {
      setSelectAllError(
        `The number of documents selected (${selectedCount}) exceeds the maximum number of references allowed`
      )
    } else {
      setSelectAllError(null)
    }
  }, [selectedCount])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    onClose?.()
  }, [onClose])

  // DataGrid columns
  const gridColumns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: t('DOCUMENT_NAME'),
        id: 'filename',
        textAlign: 'left',
        size: 300,
        enableSorting: true,
        cell: ({ cell, row }: any) => (
          <a
            href={buildUrlWithCSelections(`/show-file/${row.original.referenceFsiId}`)}
            target="_blank"
            rel="noopener noreferrer"
            title={cell.getValue()}
          >
            {cell.getValue()}
          </a>
        )
      }),
      columnHelper.accessor('artifact', {
        header: t('TABLE_TH_ARTIFACT_NO'),
        id: 'artifact',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('product', {
        header: t('EMTF_MD_PRODUCT'),
        id: 'product',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('study', {
        header: t('EMTF_MD_STUDY'),
        id: 'study',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('environment', {
        header: t('EMTF_MD_ENVIRONMENT'),
        id: 'environment',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('country', {
        header: t('EMTF_MD_COUNTRY'),
        id: 'country',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('site', {
        header: t('EMTF_MD_SITE'),
        id: 'site',
        textAlign: 'left',
        enableSorting: true
      }),
      ...(isReadOnly
        ? []
        : [
            columnHelper.display({
              id: 'actions',
              header: '',
              size: 50,
              cell: ({ row }: any) => (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRemoveReference(row.original.referenceFsiId)}
                  aria-label={t('REMOVE')}
                  className={styles.deleteIconBtn}
                >
                  <Icon>remove_circle</Icon>
                </Button>
              )
            })
          ])
    ],
    [t, isReadOnly, handleRemoveReference]
  )

  const handleGlobalSearch = useCallback((searchValue: string) => {
    setGlobalSearchValue(searchValue)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handleClearSearch = useCallback(() => {
    setGlobalSearchValue('')
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handlePaginationChange = useCallback((newPagination: { pageIndex: number; pageSize: number }) => {
    setPagination(newPagination)
  }, [])

  const handleSortingChange = useCallback((newSorting: Array<{ id: string; desc: boolean }>) => {
    setSorting(newSorting)
  }, [])

  const handleDataGridControlChange = useCallback((state: any) => {
    if (state.pagination) {
      setPagination(state.pagination)
    }
  }, [])

  if (!fsiId) return null

  return (
    <>
      <Modal show={isOpen} handleClose={handleClose} size="full" className={styles.modalBodyFlex}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <MedidataLogo />
          <Modal.Title className={styles.modalTitleCentered}>{t('BROWSER_REFERENCES_H_TITLE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={`theme-sandman ${styles.contentWrapper}`}>
            <Stack direction="horizontal" gap={2} className="mb-3 align-items-center">
              <Icon className={styles.documentIcon}>insert_drive_file</Icon>
              <Typography variant="h3">{fsiName}</Typography>
            </Stack>
            <hr className={styles.headerDivider} />

            {selectAllError && (
              <div className="alert alert-danger mb-3" role="alert">
                {selectAllError}
              </div>
            )}

            {/* Content */}
            {references.length === 0 && !isLoading ? (
              <div className={styles.emptyStateContainer}>
                <EmptyState
                  className={'fs16'}
                  variant="card"
                  defaultImgVariant="noData"
                  title={t('REFERENCES_EMPTY_TITLE', { defaultValue: 'No references to show' })}
                  subtitle={t('REFERENCES_EMPTY_SUBTITLE', {
                    defaultValue: 'You can add references by clicking the button below'
                  })}
                />
                {!isReadOnly && (
                  <Button variant="primary" size="sm" onClick={handleAddReferences} className="mt-3">
                    {t('REFERENCES_BTN_ADD', { defaultValue: 'Add references' })}
                  </Button>
                )}
              </div>
            ) : (
              <DataGrid
                key={fsiId} // Force remount when fsiId changes to reset internal state
                isLoading={isLoading}
                dataTestId="document-references-datagrid"
                columns={gridColumns}
                data={filteredReferences}
                getRowId={(row: EnrichedReference) => String(row.referenceFsiId)}
                enablePagination={true}
                manualPagination={true}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                pageCount={Math.max(1, Math.ceil(totalElements / pagination.pageSize))}
                onChangeControl={handleDataGridControlChange}
                manualDataLength={totalElements}
                rowsPerPageOptions={PAGE_SIZES_ARRAY}
                title={t('BROWSER_REFERENCES_H_TITLE')}
                enableGlobalFilter={true}
                onGlobalSearchChange={handleGlobalSearch}
                onClearSearch={handleClearSearch}
                showClearBtnInSearch={!!globalSearchValue}
                enableRowSelection={!isReadOnly}
                onRowSelectionChange={handleRowSelectionChange}
                enableSorting={true}
                manualSorting={true}
                onSortingChange={handleSortingChange}
                emptyStateOptions={{ defaultImgVariant: 'noData' }}
                isCursorPagination={true}
                dataGridBulkActionsProps={{
                  actions: [
                    ...(!isReadOnly && selectedCount >= 2
                      ? [
                          {
                            customButton: (
                              <Button
                                onClick={() => {
                                  handleBulkRemoveReferences()
                                }}
                                variant="critical"
                                size="sm"
                              >
                                {t('DELETE', { defaultValue: 'Delete' })}
                              </Button>
                            )
                          }
                        ]
                      : []),
                    {
                      customButton: (
                        <Button variant="primary" buttonStyle="ghost" size="sm" onClick={handleSelectAllOnPage}>
                          {`${t('SELECT_ALL', { defaultValue: 'Select all' })} ${filteredReferences.length}`}
                        </Button>
                      )
                    },
                    {
                      customButton: (
                        <Button variant="secondary" buttonStyle="ghost" size="sm" onClick={handleClearSelection}>
                          {t('CLEAR_SELECTION', { defaultValue: 'Clear selection' })}
                        </Button>
                      )
                    }
                  ]
                }}
                dataGridControlProps={{
                  primaryActionText: t('REFERENCES_BTN_ADD', { defaultValue: 'Add references' }),
                  primaryAction: handleAddReferences,
                  hasActionBtn: !isReadOnly,
                  hasViewOptions: false,
                  hasSecondaryActions: false,
                  searchFieldProps: {
                    maxLength: 255
                  }
                }}
                stickySelection={true}
                className="document-references-datagrid"
                state={{
                  pagination: { ...pagination, pageIndex: 0 },
                  sorting,
                  rowSelection
                }}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        title="Delete"
        show={showConfirmationDialog}
        backdrop={false}
        handleClose={handleCancel}
        isConfirmationDialog
      >
        <Modal.Body>
          {isBulkDeletePending ? (
            'Are you sure you want to delete all the selected references?'
          ) : (
            <>
              Are you sure you want to delete following references?
              <br />
              <strong>{references.find((r) => r.referenceFsiId === pendingDeleteId)?.filename}</strong>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleLeavePage}>
            Yes
          </Button>
          <Button onClick={handleCancel} variant="secondary">
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default DocumentReferencesModal
